import { algoliasearch } from "algoliasearch";
import React from "react";
import { useAsyncList } from "react-aria-components";

import type { ServerCmsContextProcessorsAlgoliaConfig } from "@reactivated";

import { HL_POST, HL_PRE } from ".";
import type { LIHit } from ".";
import { ALL_VALUE } from "../FilterableList";

type FilterValues = Record<string, string>;

const QUERY_KEY = "q";

// Encodes text query + active facet values into a single string for useAsyncList.
// ALL_VALUE entries are omitted so the query param alone means "no active facets".
const buildFilterText = (query: string, filterValues: FilterValues): string => {
    if (!query) return "";
    const params = new URLSearchParams({ [QUERY_KEY]: query });
    for (const [group, value] of Object.entries(filterValues)) {
        if (value !== ALL_VALUE) params.set(group, value);
    }
    return params.toString();
};

const parseFilterText = (
    filterText: string,
): { query: string; facets: FilterValues } => {
    const params = new URLSearchParams(filterText);
    const query = params.get(QUERY_KEY) ?? "";
    params.delete(QUERY_KEY);
    const facets: FilterValues = {};
    for (const [key, value] of params.entries()) {
        facets[key] = value;
    }
    return { query, facets };
};

// Converts active facets to Algolia facetFilters format (AND across groups).
// Each value is already a "category:slug" string (e.g. "type:findings"),
// matching the "tags.value" facetable attribute in the Algolia index.
const buildFacetFilters = (facets: FilterValues): string[][] =>
    Object.values(facets).map((value) => [`tags.value:${value}`]);

export const useAlgoliaSearch = ({
    algoliaConfig,
    initialQuery = "",
    initialFilterValues = {},
}: {
    algoliaConfig: ServerCmsContextProcessorsAlgoliaConfig;
    initialQuery?: string;
    initialFilterValues?: FilterValues;
}) => {
    const algoliaClientRef = React.useRef(
        algoliaConfig.app_id
            ? algoliasearch(algoliaConfig.app_id, algoliaConfig.search_api_key)
            : null,
    );

    const [isDirty, setIsDirty] = React.useState(!!initialQuery);
    const [totalResults, setTotalResults] = React.useState(0);
    const [hasMorePages, setHasMorePages] = React.useState(false);
    const [substituteQuery, setSubstituteQuery] = React.useState<string | null>(
        null,
    );
    const [filterValues, setFilterValues] =
        React.useState<FilterValues>(initialFilterValues);

    const list = useAsyncList<LIHit, number>({
        initialFilterText: buildFilterText(initialQuery, initialFilterValues),
        getKey: (item) => item.objectID,
        async load({ filterText, cursor, signal }) {
            const empty = { items: [] };
            const client = algoliaClientRef.current;
            if (!filterText || !client) return empty;

            const { query, facets } = parseFilterText(filterText);
            if (!query) return empty;

            const facetFilters = buildFacetFilters(facets);

            const resp = await client.searchSingleIndex<LIHit>({
                indexName: algoliaConfig.index_name,
                searchParams: {
                    page: cursor,
                    query,
                    hitsPerPage: 10,
                    highlightPreTag: HL_PRE,
                    highlightPostTag: HL_POST,
                    removeWordsIfNoResults: "lastWords",
                    attributesToHighlight: [
                        "title",
                        "subtitle",
                        "search_description",
                    ],
                    typoTolerance: true,
                    ...(facetFilters.length > 0 && { facetFilters }),
                },
            });
            if (signal.aborted) return empty;
            setIsDirty(true);
            setTotalResults(resp.nbHits ?? 0);
            if (
                resp.queryAfterRemoval &&
                resp.queryAfterRemoval !== resp.query
            ) {
                setSubstituteQuery(resp.queryAfterRemoval);
            } else {
                setSubstituteQuery(null);
            }

            const page = cursor ?? 0;
            const hasMore = !!(resp.nbPages && resp.nbPages > page + 1);
            setHasMorePages(hasMore);
            return {
                cursor: hasMore ? page + 1 : undefined,
                items: "hits" in resp ? resp.hits : [],
            };
        },
    });

    const { query } = parseFilterText(list.filterText);
    const activeQuery = substituteQuery ?? query;
    const hasSearched = isDirty && !!query;

    const setQuery = (nextQuery: string) => {
        list.setFilterText(buildFilterText(nextQuery, filterValues));
    };

    const onFilterChange = (group: string, value: string) => {
        const next = { ...filterValues, [group]: value };
        setFilterValues(next);
        list.setFilterText(buildFilterText(query, next));
    };

    const resetFilters = () => {
        setFilterValues(initialFilterValues);
        list.setFilterText(buildFilterText(query, initialFilterValues));
    };

    return {
        query,
        setQuery,
        filterValues,
        onFilterChange,
        resetFilters,
        items: list.items,
        loadMore: () => list.loadMore(),
        totalResults,
        hasMorePages,
        substituteQuery,
        activeQuery,
        hasSearched,
    };
};
